// Import dependencies
import { AutomatorSidebarPropertyChild } from '../_base';
import { unsafeCSS, css, html } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

// Import highlight.js
import hljs from 'highlight.js/lib/core';

// Import languages
import hljsJavascript from 'highlight.js/lib/languages/javascript'
import hljsXml        from 'highlight.js/lib/languages/xml' // also powers HTML :contentReference[oaicite:1]{index=1}
import hljsJson       from 'highlight.js/lib/languages/json'
import hljsPhp        from 'highlight.js/lib/languages/php'
import hljsMarkdown   from 'highlight.js/lib/languages/markdown'
import hljsCss        from 'highlight.js/lib/languages/css'
import hljsSql        from 'highlight.js/lib/languages/sql'

// Register languages
hljs.registerLanguage( 'javascript', hljsJavascript );
hljs.registerLanguage( 'xml',        hljsXml );
hljs.registerLanguage( 'html',       hljsXml ); // alias XML grammar as HTML :contentReference[oaicite:2]{index=2}
hljs.registerLanguage( 'json',       hljsJson );
hljs.registerLanguage( 'php',        hljsPhp );
hljs.registerLanguage( 'markdown',   hljsMarkdown );
hljs.registerLanguage( 'css',        hljsCss );
hljs.registerLanguage( 'sql',        hljsSql );

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-sidebar-property-code' )
export class AutomatorLogSidebarPropertyCode extends AutomatorSidebarPropertyChild {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * Defines how many lines we're going to show before showing the "Expand" button
	 */
	numberOfLinesVisible = 10;

	// Custom properties (doesn't apply to other properties)
	@property( { type: Boolean, attribute: false } ) accessor isCollapsed = true;

	@property( { type: Boolean, attribute: false } ) accessor shouldHighlight = false;

	/**
	 * Checks if the value or attributes changed and sees if it has to format the value;
	 *
	 * @param {Map} changedProperties The changed properties
	 */
	willUpdate( changedProperties ) {
		if ( changedProperties.has( 'value' ) || changedProperties.has( 'attributes' ) ) {
			this.maybeFormatJSON();
		}
	}

	/**
	 * Formats the value if it's a JSON
	 */
	maybeFormatJSON() {
		if ( this.attributes.code_language !== 'json' ) {
			return;
		}

		try {
			this.value = JSON.stringify( JSON.parse( this.value ), null, 2 );
		} catch ( e ) {}
	}

	/**
	 * Handles the click of the button that toggles the collapsed/expanded status
	 */
	handleCollapseToggle() {
		this.isCollapsed = ! this.isCollapsed;
	}

	/**
	 * Highlights the code on hover
	 */
	handleHighlightOnHover() {
		this.shouldHighlight = true;
	}

	/**
	 * Returns the template for the label of the property
	 *
	 * @return {TemplateResult|undefined} The label of the property
	 */
	get _templateLabel() {
		// Check if the label is defined
		if ( isEmpty( this.label ) ) {
			return;
		}

		return html`
			<div class="property-label">
				${ this.label }

				${
					! isEmpty( this.attributes.code_language ) ?
					html`
						<uo-chip size="small" filled>${ this.attributes.code_language.toUpperCase() }</uo-chip>
					` : ''
				}
			</div>
		`;
	}

	/**
	 * Returns the template to render the value of the property
	 *
	 * @return {TemplateResult} The value of the property
	 */
	get _templateValue() {
		return html`
			<div 
				class="property-code-view ${ this.canBeExpanded ? 'property-code-view--can-be-expanded' : '' }"
				@mouseover=${ this.handleHighlightOnHover }
			>
				<div 
					class="property-code-view-content"
					style="${ this.isCollapsed ? `max-height: ${ ( ( this.numberOfLinesVisible + 1 ) * 1.25 ) }em` : '' }"
				>
					<pre><code>${ this._templateValueCode }</code></pre>
				</div>
				
				${ this._templateExpandButton }
			</div>			
		`;
	}

	/**
	 * Returns the code, it could be highlighted
	 *
	 * @return {TemplateResult} The code (value)
	 */
	get _templateValueCode() {
		// Check if we have to show it as-is
		if ( ! this.shouldHighlight ) {
			return html`${ this.value }`;
		}

		return html`${
			// The "unsafe HTML" here is the HTML created by highlight.js
			// The code that highlight.js is highlighting is escaped, so it won't be executed
			unsafeHTML(
				hljs.highlightAuto(
					this.value
				).value
			)
		}`;
	}

	/**
	 * Returns the expand/collapse button
	 *
	 * @return {TemplateResult|undefined} The expand/collapse button or undefined if it shouldn't be shown
	 */
	get _templateExpandButton() {
		if ( ! this.canBeExpanded ) {
			return;
		}

		return html`
			<div 
				class="property-code-view-toggle"
				@click=${ this.handleCollapseToggle }
			>
				${
					this.isCollapsed ?
					html`
						${ sprintf(
							/* translators: 1. Number of lines */
							escapeHTML( __( 'Expand %1$s lines', 'uncanny-automator' ) ),
							( this.numberOfLines - this.numberOfLinesVisible )
						) }
						<uo-icon id="angle-down"></uo-icon>
					` :
					html`
						${ escapeHTML( __( 'Collapse', 'uncanny-automator' ) ) }
						<uo-icon id="angle-up"></uo-icon>
					`
				}
			</div>
		`;
	}

	/**
	 * Checks whether there is enough content to justify adding the "Expand" button
	 *
	 * @return {Boolean} Whether there is enough content to justify adding the "Expand" button
	 */
	get canBeExpanded() {
		return this.numberOfLines > this.numberOfLinesVisible;
	}

	/**
	 * Returns the number of lines in the value
	 *
	 * @return {Number} The number of lines in the value
	 */
	get numberOfLines() {
		return this.value.split( /\r\n|\r|\n/ ).length;
	}
}
